import React, { useMemo, useState } from 'react';
import { ArrowLeft, Bot, Camera, CheckCircle2, Store } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  publicarProductoEmprendedor,
  registrarEmprendedor,
  sugerirProductoIaEmprendedor,
  type ComercioStore,
} from '../services/marketplaceService.tsx';

export default function EmprendedoresOnboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [perfil, setPerfil] = useState({
    nombre_marca: '',
    rubro: '',
    telefono: '',
    whatsapp: '',
    bio: '',
    email: '',
    password: '',
  });

  const [comercio, setComercio] = useState<ComercioStore | null>(null);
  const [foto, setFoto] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [producto, setProducto] = useState({
    nombre: '',
    descripcion: '',
    precio: 0,
    stock: 1,
  });

  const progreso = useMemo(() => (step === 1 ? 33 : step === 2 ? 66 : 100), [step]);

  const crearPerfil = async () => {
    setError('');
    if (!perfil.nombre_marca.trim() || !perfil.rubro.trim() || !perfil.telefono.trim() || !perfil.email.trim() || perfil.password.length < 6) {
      setError('Completá nombre/marca, rubro, teléfono, email y una contraseña (mínimo 6).');
      return;
    }
    try {
      setLoading(true);
      const created = await registrarEmprendedor(perfil);
      setComercio(created.comercio);
      localStorage.removeItem('emprendedorToken');
      setStep(3);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo crear el perfil emprendedor.');
    } finally {
      setLoading(false);
    }
  };

  const sugerirConIa = async () => {
    if (!comercio) return;
    try {
      setLoading(true);
      setError('');
      const sug = await sugerirProductoIaEmprendedor({
        nombre_marca: perfil.nombre_marca,
        rubro: perfil.rubro,
        foto_nombre: foto?.name,
        imageFile: foto,
      });
      setProducto({
        nombre: sug.titulo,
        descripcion: sug.descripcion,
        precio: Number(sug.precio_sugerido || 0),
        stock: 1,
      });
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo generar sugerencia IA.');
    } finally {
      setLoading(false);
    }
  };

  const publicar = async () => {
    if (!comercio) return;
    if (!producto.nombre.trim()) {
      setError('Completá el nombre del producto.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await publicarProductoEmprendedor({
        comercio_id: comercio.comercio_id,
        nombre: producto.nombre,
        descripcion: producto.descripcion,
        precio: Number(producto.precio || 0),
        stock: Number(producto.stock || 1),
      });
      setStep(3);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo publicar el producto.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#eef6ff] p-4 sm:p-6">
      <div className="mx-auto max-w-3xl space-y-4">
        <button onClick={() => navigate('/catalogo')} className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700">
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>

        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <h1 className="text-2xl font-bold text-slate-900">Registro de Emprendedores</h1>
          <p className="mt-1 text-sm text-slate-600">Entrá al marketplace sin trámites complicados. Después te ayudamos a formalizar.</p>
          <div className="mt-3 h-2 overflow-hidden rounded bg-slate-100">
            <div className="h-full bg-sky-600 transition-all" style={{ width: `${progreso}%` }} />
          </div>
        </div>

        {error && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

        {step === 1 && (
          <div className="rounded-2xl bg-white p-5 shadow-sm space-y-3">
            <h2 className="text-lg font-semibold">1) Crear perfil</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <input className="rounded border p-2" placeholder="Nombre o marca" value={perfil.nombre_marca} onChange={(e) => setPerfil({ ...perfil, nombre_marca: e.target.value })} />
              <input className="rounded border p-2" placeholder="Rubro" value={perfil.rubro} onChange={(e) => setPerfil({ ...perfil, rubro: e.target.value })} />
              <input className="rounded border p-2" placeholder="Teléfono" value={perfil.telefono} onChange={(e) => setPerfil({ ...perfil, telefono: e.target.value })} />
              <input className="rounded border p-2" placeholder="WhatsApp (opcional)" value={perfil.whatsapp} onChange={(e) => setPerfil({ ...perfil, whatsapp: e.target.value })} />
              <input className="rounded border p-2" type="email" placeholder="Email para volver a tu cuenta" value={perfil.email} onChange={(e) => setPerfil({ ...perfil, email: e.target.value })} />
              <input className="rounded border p-2" type="password" placeholder="Contraseña (mínimo 6)" value={perfil.password} onChange={(e) => setPerfil({ ...perfil, password: e.target.value })} />
              <textarea className="sm:col-span-2 rounded border p-2" rows={3} placeholder="Contanos qué vendés (opcional)" value={perfil.bio} onChange={(e) => setPerfil({ ...perfil, bio: e.target.value })} />
            </div>
            <div className="flex items-center justify-between">
              <button onClick={() => navigate('/emprendedores/cuenta')} className="text-sm font-semibold text-indigo-700 hover:text-indigo-900">
                Ya tengo cuenta emprendedora
              </button>
              <button disabled={loading} onClick={crearPerfil} className="rounded bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {loading ? 'Creando...' : 'Crear perfil'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="rounded-2xl bg-white p-5 shadow-sm space-y-3">
            <h2 className="text-lg font-semibold">2) Publicar producto con IA</h2>
            <label className="block rounded border border-dashed p-3 text-sm text-slate-600">
              <span className="inline-flex items-center gap-2 font-semibold text-slate-800"><Camera className="h-4 w-4" /> Foto del producto (opcional)</span>
              <input
                type="file"
                accept="image/*"
                className="mt-2 block w-full"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setFoto(f);
                  setPreview(f ? URL.createObjectURL(f) : '');
                }}
              />
            </label>
            {preview && <img src={preview} alt="preview" className="h-48 w-full rounded object-cover" />}
            <button onClick={sugerirConIa} disabled={loading} className="inline-flex items-center gap-2 rounded border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 disabled:opacity-60">
              <Bot className="h-4 w-4" /> {loading ? 'Analizando...' : 'Sugerir con IA'}
            </button>

            <div className="grid gap-3">
              <input className="rounded border p-2" placeholder="Título" value={producto.nombre} onChange={(e) => setProducto({ ...producto, nombre: e.target.value })} />
              <textarea className="rounded border p-2" rows={4} placeholder="Descripción" value={producto.descripcion} onChange={(e) => setProducto({ ...producto, descripcion: e.target.value })} />
              <div className="grid gap-3 sm:grid-cols-2">
                <input type="number" className="rounded border p-2" placeholder="Precio" value={producto.precio} onChange={(e) => setProducto({ ...producto, precio: Number(e.target.value || 0) })} />
                <input type="number" className="rounded border p-2" placeholder="Stock" value={producto.stock} onChange={(e) => setProducto({ ...producto, stock: Number(e.target.value || 1) })} />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <button className="rounded border px-3 py-2 text-sm" onClick={() => setStep(1)}>Atrás</button>
              <button disabled={loading} onClick={publicar} className="rounded bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {loading ? 'Publicando...' : 'Publicar en marketplace'}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="rounded-2xl bg-white p-6 shadow-sm text-center space-y-3">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <h2 className="text-xl font-bold text-slate-900">Registro enviado ✅</h2>
            <p className="text-sm text-slate-600">
              Tu usuario emprendedor quedó <b>pendiente de aprobación del administrador</b> para mostrarse en el catálogo público.
              Ya podés ingresar a tu cuenta y cargar productos, pero no se verán en el catálogo hasta ser aprobado.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <button onClick={() => navigate('/catalogo')} className="inline-flex items-center gap-2 rounded bg-sky-600 px-4 py-2 text-sm font-semibold text-white">
                <Store className="h-4 w-4" /> Volver al marketplace
              </button>
              <button onClick={() => navigate('/institucional')} className="rounded border px-4 py-2 text-sm font-semibold text-slate-700">
                Quiero formalizarme con la Cámara
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
